// @ts-check
// Teclado nos gráficos em SVG (contrato 07, seção 10): o gráfico é uma parada só de Tab e, dentro dele, as setas
// percorrem os pontos que abrem uma lista (tabindex rotativo). Esquerda e direita mudam a categoria, cima e baixo
// mudam a série (nas barras horizontais, ao contrário, porque as categorias ficam uma embaixo da outra), Home e End
// vão ao primeiro e ao último ponto, Enter abre a lista na mesma aba e Ctrl/Cmd+Enter numa aba nova. Módulo puro.

/**
 * @typedef {{ categoryIndex: number, seriesIndex: number }} ChartKeyboardPoint
 * @typedef {{ type: 'move', index: number } | { type: 'open', newTab: boolean } | null} ChartKeyboardAction
 * @typedef {{ key: string, ctrlKey?: boolean, metaKey?: boolean, altKey?: boolean, shiftKey?: boolean }} ChartKeyboardEvent
 */

/**
 * Ponto mais perto da série atual numa categoria (empate: a série de cima, a de índice menor).
 * @param {ChartKeyboardPoint[]} points @param {number} categoryIndex @param {number} seriesIndex
 */
function closestInCategory(points, categoryIndex, seriesIndex) {
  let best = -1;
  points.forEach((point, index) => {
    if (point.categoryIndex !== categoryIndex) return;
    if (best === -1) {
      best = index;
      return;
    }
    const distance = Math.abs(point.seriesIndex - seriesIndex);
    const bestDistance = Math.abs(points[best].seriesIndex - seriesIndex);
    if (distance < bestDistance || (distance === bestDistance && point.seriesIndex < points[best].seriesIndex)) best = index;
  });
  return best;
}

/**
 * Próxima categoria com algum ponto, na direção dada (-1 ou 1); -1 quando não há.
 * @param {ChartKeyboardPoint[]} points @param {ChartKeyboardPoint} current @param {number} direction
 */
function nextCategory(points, current, direction) {
  const categories = [...new Set(points.map((point) => point.categoryIndex))].sort((left, right) => left - right);
  const position = categories.indexOf(current.categoryIndex);
  const target = categories[position + direction];
  return target === undefined ? -1 : target;
}

/**
 * Próximo ponto na mesma categoria, na direção da série; -1 quando não há.
 * @param {ChartKeyboardPoint[]} points @param {ChartKeyboardPoint} current @param {number} direction
 */
function nextSeries(points, current, direction) {
  const candidates = points
    .map((point, index) => ({ point, index }))
    .filter(({ point }) => point.categoryIndex === current.categoryIndex && Math.sign(point.seriesIndex - current.seriesIndex) === direction)
    .sort((left, right) => direction * (left.point.seriesIndex - right.point.seriesIndex));
  return candidates.length > 0 ? candidates[0].index : -1;
}

/**
 * O que uma tecla faz no gráfico: mover o foco para outro ponto, abrir a lista ou nada (null, a tecla segue o curso).
 * Nas bordas o foco fica onde está, sem rolar a página.
 * @param {ChartKeyboardPoint[]} points pontos focáveis, na ordem de leitura
 * @param {number} currentIndex ponto com o foco agora
 * @param {ChartKeyboardEvent} event
 * @param {{ orientation?: 'vertical' | 'horizontal' }} [options] horizontal: barras deitadas, categorias de cima para baixo
 * @returns {ChartKeyboardAction}
 */
export function chartKeyboardAction(points, currentIndex, event, { orientation = 'vertical' } = {}) {
  if (points.length === 0) return null;
  const index = Math.max(0, Math.min(points.length - 1, Number.isInteger(currentIndex) ? currentIndex : 0));
  const current = points[index];
  const isHorizontal = orientation === 'horizontal';
  const categoryKeys = isHorizontal ? { previous: 'ArrowUp', next: 'ArrowDown' } : { previous: 'ArrowLeft', next: 'ArrowRight' };
  const seriesKeys = isHorizontal ? { previous: 'ArrowLeft', next: 'ArrowRight' } : { previous: 'ArrowUp', next: 'ArrowDown' };
  switch (event.key) {
    case 'Enter':
      return { type: 'open', newTab: Boolean(event.ctrlKey || event.metaKey) };
    case 'Home':
      return { type: 'move', index: 0 };
    case 'End':
      return { type: 'move', index: points.length - 1 };
    case categoryKeys.previous:
    case categoryKeys.next: {
      const category = nextCategory(points, current, event.key === categoryKeys.next ? 1 : -1);
      return { type: 'move', index: category === -1 ? index : closestInCategory(points, category, current.seriesIndex) };
    }
    case seriesKeys.previous:
    case seriesKeys.next: {
      const target = nextSeries(points, current, event.key === seriesKeys.next ? 1 : -1);
      return { type: 'move', index: target === -1 ? index : target };
    }
    default:
      return null;
  }
}
